import React, { useContext } from "react";
import {
  Box,
  Group,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { IconId, IconRefresh } from "@tabler/icons-react";
import { DataContext } from "../../../context/DataContext";
import useLocale from "../../../hooks/useLocale";

// Barcode-first intro for the dcams/blinkid route (design: "BlinkID
// Flow v2", screen 01). Replaces the generic document-capture intro
// when the step scans the barcode side only: an ID-flip illustration,
// "skip the typing" pitch, and — when the route step has an active
// fallback — a "No barcode? Use a different ID" escape hatch straight
// into ID document picture verification.
//
// Renders only the scrollable intro content; the camera-permission
// note and the "Scan My Barcode" button stay in the Dcams page's
// bottom slot like every other capture flow.
export default function BlinkIdBarcodeIntro({ onUseDifferentId }) {
  const { data } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("document-capture");
  const dark = theme.colorScheme === "dark";

  // data.styles.primaryColor may be a Mantine color name or a raw CSS
  // color; resolve names through the theme, pass raw values through.
  const primaryName = data?.styles?.primaryColor || "teal";
  const primary = theme.colors[primaryName]?.[6] || primaryName;

  const cardFace = dark ? theme.colors.dark[5] : theme.colors.gray[1];
  const cardLine = dark ? theme.colors.dark[3] : theme.colors.gray[3];
  const cardBg = dark ? theme.colors.dark[7] : theme.white;
  const barcodeInk = dark ? theme.colors.gray[3] : theme.colors.dark[9];
  const barcode = `repeating-linear-gradient(90deg, ${barcodeInk} 0 3px, transparent 3px 6px, ${barcodeInk} 6px 7px, transparent 7px 11px)`;

  return (
    <Stack align="center" spacing="lg" pt="md">
      {/* ID flip illustration: front of the card behind, barcode side in
          front, refresh badge marking the flip. */}
      <Box style={{ position: "relative", width: 210, height: 136 }}>
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 176,
            height: 110,
            borderRadius: 10,
            background: cardFace,
            border: `1px solid ${cardLine}`,
            padding: 10,
            boxSizing: "border-box",
            display: "flex",
            gap: 8,
          }}
        >
          <Box style={{ width: 32, height: 40, borderRadius: 4, background: cardLine }} />
          <Box style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, paddingTop: 2 }}>
            <Box style={{ height: 5, width: "80%", background: cardLine, borderRadius: 3 }} />
            <Box style={{ height: 5, width: "60%", background: cardLine, borderRadius: 3, opacity: 0.7 }} />
            <Box style={{ height: 5, width: "70%", background: cardLine, borderRadius: 3, opacity: 0.7 }} />
          </Box>
        </Box>
        <Box
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 176,
            height: 110,
            borderRadius: 10,
            background: cardBg,
            border: `2px solid ${primary}`,
            boxShadow: `0 6px 16px ${primary}33`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box style={{ width: 134, height: 54, background: barcode }} />
        </Box>
        <Box
          style={{
            position: "absolute",
            top: 46,
            left: 156,
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 3px 8px ${primary}59`,
          }}
        >
          <IconRefresh size={16} stroke={2.5} color={theme.white} />
        </Box>
      </Box>

      <Text fz={28} fw={700} align="center" style={{ lineHeight: 1.2 }}>
        {locale?.barcodeIntro?.title || "Skip the typing — use your ID barcode"}
      </Text>
      <Text fz="md" c="dimmed" align="center" px="xs" style={{ lineHeight: 1.55 }}>
        {locale?.barcodeIntro?.subtitle ||
          "One photo fills in your details for you. We then check them against trusted and verified data sources."}
      </Text>

      {onUseDifferentId && (
        <UnstyledButton onClick={onUseDifferentId} style={{ alignSelf: "stretch" }}>
          <Group
            spacing={8}
            noWrap
            position="center"
            style={{
              background: theme.fn.rgba(primary, dark ? 0.15 : 0.08),
              border: `1px solid ${theme.fn.rgba(primary, 0.45)}`,
              borderRadius: 10,
              padding: "12px 16px",
            }}
          >
            <IconId size={20} stroke={1.7} color={primary} />
            <Text fz="md" c="dimmed">
              {locale?.barcodeIntro?.noBarcode || "No barcode?"}
            </Text>
            <Text fz="md" fw={700} style={{ color: primary }}>
              {locale?.barcodeIntro?.useDifferentId || "Use a different ID"}
            </Text>
          </Group>
        </UnstyledButton>
      )}
    </Stack>
  );
}
